"use client";

import { cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";

import { cn } from "../../utils";
import { Button, type ButtonBaseProps } from "../button";
import { Popover } from "../popover";
import { useComboboxContext } from "./context";

export interface ComboboxTriggerProps extends ButtonBaseProps {
  icon?: boolean;
  children?: ReactNode;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  asChild?: boolean;
}

const Trigger = ({
  ref,
  className,
  children,
  placeholder,
  isInvalid,
  isWarning,
  color,
  asChild = true,
  ...props
}: ComboboxTriggerProps) => {
  const { open, setOpen, withoutPopover, isMobile } = useComboboxContext();

  if (withoutPopover || isMobile) {
    const handleClick = (e: React.MouseEvent) => {
      if (props.disabled) {
        return;
      }
      // Preserve any child onClick
      // @ts-expect-error safe optional chaining
      children?.props?.onClick?.(e);
      setOpen(!open);
    };

    if (asChild && children && isValidElement(children)) {
      // eslint-disable-next-line react-hooks/refs
      return cloneElement(children, {
        ...props,
        color,
        className: cn((children as ReactElement<{ className?: string }>).props?.className, className),
        onClick: handleClick,
        ref,
        "data-state": open ? "open" : "closed",
        "aria-disabled": props.disabled,
      } as never);
    }

    if (children) {
      return (
        <div
          // A styled trigger for a popover listbox, not a native `select`.
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role, jsx-a11y/role-has-required-aria-props
          role="combobox"
          aria-expanded={open}
          aria-disabled={props.disabled}
          tabIndex={props.disabled ? -1 : 0}
          className={className}
          onClick={handleClick}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick(e as unknown as React.MouseEvent);
            }
          }}
          data-state={open ? "open" : "closed"}
        >
          {children}
        </div>
      );
    }

    return (
      <div className={className}>
        {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role, jsx-a11y/role-has-required-aria-props */}
        <Button role="combobox" onClick={() => setOpen(!open)} {...props}>
          {placeholder}
        </Button>
      </div>
    );
  }

  return (
    <Popover.Trigger ref={ref} className={className} asChild={asChild} {...props}>
      {children || (
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role, jsx-a11y/role-has-required-aria-props -- Radix sets `aria-expanded`/`aria-controls` on the trigger
        <Button role="combobox" {...props}>
          {placeholder}
        </Button>
      )}
    </Popover.Trigger>
  );
};

Trigger.displayName = "ComboboxTrigger";

export default Trigger;
